import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import { useIntercom } from "react-use-intercom";
import { CircularProgress, Select, MenuItem, Skeleton } from "@mui/material";
import { useQueryClient } from "react-query";
import CheckBox from "../../../Components/Form Fields/checkbox";
import EsignDisclosureViewer from "../../../Components/Others/esign-disclosure-viewer";

import { useModalContext } from "../../../Contexts/ModalContext";
import { useActivities, useConnectedAccounts, useDeals, useStakeDebit, useSsoPrefill } from "../../../CustomHooks/Queries";
import { useApplyStakeChecking, useRequestStakeDebitCard, useSaveStakeCheckingProgressState } from "../../../CustomHooks/Mutations";
import useConnectBankAccount from "../../../CustomHooks/useConnectBankAccount";
import { readSyncteraBrandStyles, useSyncteraWidgetScripts } from "../../../CustomHooks/useSyncteraWidgets";

import AddressAutoCompleteField from "../../../Components/Form Fields/address-autocomplete-field";
import { APIQueryKeys, CountriesList, modalVariants, UnitOccupationList } from "../../../Utils/constants";
import {
  appendISOTime,
  dispatchAPIErrorToast,
  hasSVDPhysicalCard,
  hasSVDVirtualCard,
  isSVDApplicationApproved,
  isSVDApproved,
  isSVDClosedForStakeSaverNonCompliance,
  isSVDDepositAccountCreated,
  isSVDDenied,
  isSVDEligible,
  isSVDIneligible,
  isSVDPending,
  isSVDPendingVerification,
  isSVDReview,
  isSVDVerificationPendingStatus,
  isSVDVerificationRejected,
  BATTLECARD_FLOW_NAV_STATE,
  BATTLECARD_LEGACY_ONLY_STATE,
  isBattlecardRoute,
  transformCountryCodeFormatToUSNumber,
} from "../../../Utils/helpers";
import { citizenshipCodeOrLabelToLabel } from "../../../Utils/sso-prefill-normalize";
import { validation } from "../../../Utils/validations";

import stakeDebitImg from "../../../Assets/Images/stake-debit.png";
import renderGetBankAmountDetails from "../Add Funds/get-bank-amount-details";
import renderSetPin, { SetPinModal } from "../Stake Debit/set-pin";

/* ═══════════════════════════════════════════
   INPUT FORMATTERS
   ═══════════════════════════════════════════ */

function formatSSN(e) {
  const raw = e.target.value.replace(/\D/g, "").slice(0, 9);
  let formatted = "";
  if (raw.length > 0) formatted = raw.slice(0, 3);
  if (raw.length > 3) formatted += "-" + raw.slice(3, 5);
  if (raw.length > 5) formatted += "-" + raw.slice(5, 9);
  e.target.value = formatted;
}

function getInputByLabel(container, labelText) {
  const labels = container.querySelectorAll(".ob-outlined-label");
  for (const label of labels) {
    if (label.textContent === labelText)
      return label.closest(".ob-outlined").querySelector(".ob-outlined-input");
  }
  return null;
}

function normalizeAddressField(value) {
  return (value || "").trim();
}

function hasAddressChangedFromPrefill(prefill, payload) {
  if (!prefill) return false;

  const normalizeZip = (value) => (value || "").replace(/\D/g, "").slice(0, 5);

  return (
    normalizeAddressField(prefill.mailingstreet) !== normalizeAddressField(payload.street_address) ||
    normalizeAddressField(prefill.apt_number) !== normalizeAddressField(payload.apt_number) ||
    normalizeAddressField(prefill.mailingcity).toLowerCase() !==
      normalizeAddressField(payload.city).toLowerCase() ||
    normalizeAddressField(prefill.mailingstate).toUpperCase() !==
      normalizeAddressField(payload.mailing_state).toUpperCase() ||
    normalizeZip(prefill.mailingpostalcode) !== normalizeZip(payload.zip_code)
  );
}

function hasAddressMatchingPrefill(prefill, payload) {
  if (!prefill) return false;
  return !hasAddressChangedFromPrefill(prefill, payload);
}

function shouldShowUmfAddressConfirmModal(isUmfUser, prefillBaseline, addressPayload) {
  return Boolean(
    isUmfUser &&
    prefillBaseline &&
    hasAddressMatchingPrefill(prefillBaseline, addressPayload),
  );
}

function showStepError(container, message) {
  const err = container.querySelector(".ob-step-error");
  if (err) {
    err.textContent = message;
    err.style.display = "block";
  }
}

function clearAllFieldErrors(container) {
  if (!container) return;
  container.querySelectorAll(".ob-field-error").forEach((err) => {
    err.textContent = "";
    err.style.display = "none";
  });
  container.querySelectorAll(".ob-field-group.has-error").forEach((group) => {
    group.classList.remove("has-error");
  });
  const stepErr = container.querySelector(".ob-step-error");
  if (stepErr) {
    stepErr.textContent = "";
    stepErr.style.display = "none";
  }
}

function applyFieldErrors(container, errors) {
  clearAllFieldErrors(container);
  (errors || []).forEach(({ label, message }) => {
    if (!message) return;
    const group = container?.querySelector(`[data-field-label="${label}"]`);
    const err = group?.querySelector(".ob-field-error");
    if (err) {
      err.textContent = message;
      err.style.display = "block";
      group.classList.add("has-error");
    }
  });
}

function clearStepError(e) {
  clearFieldErrorForInput(e);
}

function clearStepErrorFromContainer(el) {
  clearFieldErrorForInput({ target: el });
}

function clearFieldErrorForInput(e) {
  const target = e?.target;
  if (typeof target?.closest !== "function") return;

  const group = target.closest(".ob-field-group");
  if (group) {
    const err = group.querySelector(".ob-field-error");
    if (err) {
      err.textContent = "";
      err.style.display = "none";
    }
    group.classList.remove("has-error");
    return;
  }

  const err = target.closest(".ob-form-body")?.querySelector(".ob-step-error");
  if (err) err.style.display = "none";
}

function FieldGroup({ label, children, className = "" }) {
  return (
    <div
      className={`ob-field-group${className ? ` ${className}` : ""}`}
      data-field-label={label}
    >
      {children}
      <div className="ob-error-text ob-field-error" aria-live="polite" />
    </div>
  );
}

function handleCitizenshipChange(e) {
  const isOther = e.target.value && e.target.value !== "United States";
  const err = e.target.closest(".ob-form-body")?.querySelector(".ob-error-text");
  if (err) err.style.display = isOther ? "block" : "none";
}

/* ═══════════════════════════════════════════
   MUI-STYLE OUTLINED INPUT / SELECT
   ═══════════════════════════════════════════ */
function ObInput({ label, prefix, ...props }) {
  const [focused, setFocused] = useState(false);
  const [hasValue, setHasValue] = useState(
    !!props.defaultValue || !!props.value,
  );
  const floated = focused || hasValue || !!props.value;
  const inputRef = useRef(null);

  useEffect(() => {
    if (props.defaultValue && !hasValue) setHasValue(true);
  }, [props.defaultValue]);

  useEffect(() => {
    if (props.defaultValue && inputRef.current && !inputRef.current.value) {
      inputRef.current.value = props.defaultValue;
    }
  }, [props.defaultValue]);

  const handleAnimationStart = (e) => {
    if (e.animationName === "mui-auto-fill") setHasValue(true);
    if (e.animationName === "mui-auto-fill-cancel")
      setHasValue(!!e.target.value);
  };

  return (
    <div
      className={`ob-outlined${floated ? " floated" : ""}${focused ? " focused" : ""}${prefix ? " has-prefix" : ""}`}
    >
      <label className="ob-outlined-label">{label}</label>
      {prefix && floated && (
        <span className="ob-outlined-prefix">{prefix}</span>
      )}
      <input
        {...props}
        ref={inputRef}
        spellCheck={false}
        className="ob-outlined-input"
        style={prefix && floated ? { paddingLeft: 32 } : undefined}
        onFocus={(e) => {
          setFocused(true);
          props.onFocus?.(e);
        }}
        onBlur={(e) => {
          setFocused(false);
          setHasValue(!!e.target.value);
          props.onBlur?.(e);
        }}
        onInput={(e) => {
          setHasValue(!!e.target.value);
          props.onInput?.(e);
        }}
        onAnimationStart={handleAnimationStart}
        placeholder={floated ? props.placeholder || "" : ""}
      />
    </div>
  );
}

function ObSelect({ label, children, hasValue: externalHasValue, defaultValue, onChange, ...props }) {
  const [focused, setFocused] = useState(false);
  const [value, setValue] = useState(defaultValue ?? "");
  const hiddenRef = useRef(null);
  const userChangedRef = useRef(false);
  const hasValue = externalHasValue !== undefined ? externalHasValue : !!value;
  const floated = focused || hasValue;

  // Prefill can land after the first render; follow it until the member picks something themselves.
  useEffect(() => {
    if (defaultValue && !userChangedRef.current) setValue(defaultValue);
  }, [defaultValue]);

  useEffect(() => {
    if (defaultValue && !value && !userChangedRef.current) setValue(defaultValue);
  }, [value, defaultValue]);

  return (
    <div
      className={`ob-outlined ob-outlined-select${floated ? " floated" : ""}${focused ? " focused" : ""}`}
    >
      <label className="ob-outlined-label">{label}</label>
      <input type="hidden" className="ob-outlined-input" value={value} readOnly ref={hiddenRef} />
      <Select
        value={value}
        displayEmpty
        className="ob-mui-select"
        onOpen={() => setFocused(true)}
        onClose={() => setFocused(false)}
        onChange={(e) => {
          const newVal = e.target.value;
          userChangedRef.current = true;
          setValue(newVal);
          if (hiddenRef.current) {
            hiddenRef.current.value = newVal;
            onChange?.({ target: hiddenRef.current, currentTarget: hiddenRef.current });
          } else {
            onChange?.(e);
          }
        }}
        MenuProps={{
          PaperProps: { className: "ob-select-dropdown" },
        }}
        {...props}
      >
        {children}
      </Select>
    </div>
  );
}

function SSNInput({ label, ssn, ...props }) {
  const formatted = useMemo(() => {
    if (!ssn) return "";
    const digits = ssn.replace(/\D/g, "");
    if (digits.length !== 9) return "";
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5, 9)}`;
  }, [ssn]);

  return (
    <div className="ob-ssn-wrap">
      <ObInput
        label={label}
        placeholder={props.placeholder}
        maxLength={props.maxLength}
        defaultValue={formatted}
        onInput={(e) => {
          formatSSN(e);
          clearFieldErrorForInput(e);
        }}
      />
    </div>
  );
}


export const ObCheckSvg = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M4 8L7 11L12 5"
      stroke="#0E1220"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronRight = () => (
  <svg width="8" height="14" viewBox="0 0 8 14" fill="none">
    <path
      d="M1 1L7 7L1 13"
      stroke="#6C7086"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* ═══════════════════════════════════════════
   DOB CALENDAR PICKER
   ───────────────────────────────────────────
   Inline pattern: a typeable MM/DD/YYYY input on top, with the
   calendar permanently visible underneath. No click-to-open.
   The input and the calendar stay in two-way sync.
   ═══════════════════════════════════════════ */
const DOB_MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const DOB_DAYS = ["S", "M", "T", "W", "T", "F", "S"];

function formatDOB(m, d, y) {
  return `${String(m + 1).padStart(2, "0")}/${String(d).padStart(2, "0")}/${y}`;
}

function maskDateInput(value) {
  // Keep digits only, then weave in slashes after MM and DD.
  const digits = value.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

function parseDOB(value) {
  const m = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return null;
  const mm = +m[1], dd = +m[2], yyyy = +m[3];
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return null;
  // Reject impossible calendar dates like 02/30.
  const d = new Date(yyyy, mm - 1, dd);
  if (d.getMonth() !== mm - 1 || d.getDate() !== dd || d.getFullYear() !== yyyy) return null;
  return { d: dd, m: mm - 1, y: yyyy };
}

function DOBCalendar({ initialDate }) {
  const initial = useMemo(() => {
    if (!initialDate) return null;
    const d = new Date(initialDate);
    return isNaN(d.getTime()) ? null : { d: d.getDate(), m: d.getMonth(), y: d.getFullYear() };
  }, [initialDate]);

  const [selectedDate, setSelectedDate] = useState(initial);
  const [inputValue, setInputValue] = useState(() => (initial ? formatDOB(initial.m, initial.d, initial.y) : ""));
  const [viewMonth, setViewMonth] = useState(initial?.m ?? 0);
  const [viewYear, setViewYear] = useState(initial?.y ?? 1998);
  const [showYearPicker, setShowYearPicker] = useState(false);
  const yearGridRef = useRef(null);
  const calRef = useRef(null);

  const currentYear = new Date().getFullYear();
  const minYear = currentYear - 100;
  const maxYear = currentYear - 18;
  const years = useMemo(() => {
    const out = [];
    for (let y = maxYear; y >= minYear; y--) out.push(y);
    return out;
  }, [maxYear, minYear]);

  useEffect(() => {
    if (showYearPicker && yearGridRef.current) {
      const sel = yearGridRef.current.querySelector(".dob-year-btn.active");
      if (sel) sel.scrollIntoView({ block: "center", behavior: "instant" });
    }
  }, [showYearPicker]);

  function daysInMonth(m, y) { return new Date(y, m + 1, 0).getDate(); }
  function firstDayOfMonth(m, y) { return new Date(y, m, 1).getDay(); }

  function prevMonth() {
    if (viewMonth === 0) {
      if (viewYear - 1 < minYear) return;
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else setViewMonth((m) => m - 1);
  }
  function nextMonth() {
    if (viewMonth === 11) {
      if (viewYear + 1 > maxYear) return;
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else setViewMonth((m) => m + 1);
  }
  function prevYear() {
    if (viewYear - 1 < minYear) return;
    setViewYear((y) => y - 1);
  }
  function nextYear() {
    if (viewYear + 1 > maxYear) return;
    setViewYear((y) => y + 1);
  }

  function selectYear(y) {
    setViewYear(y);
    setShowYearPicker(false);
  }

  function selectDay(day) {
    const next = { d: day, m: viewMonth, y: viewYear };
    setSelectedDate(next);
    setInputValue(formatDOB(viewMonth, day, viewYear));
    setShowYearPicker(false);
    clearStepErrorFromContainer(calRef.current);
  }

  function handleInputChange(e) {
    const masked = maskDateInput(e.target.value);
    setInputValue(masked);
    clearStepErrorFromContainer(calRef.current);
    const parsed = parseDOB(masked);
    if (parsed) {
      setSelectedDate(parsed);
      // Sync calendar viewport when the typed year is reachable via the nav.
      if (parsed.y >= minYear && parsed.y <= maxYear) {
        setViewMonth(parsed.m);
        setViewYear(parsed.y);
      }
    } else {
      setSelectedDate(null);
    }
  }

  function handleInputKeyDown(e) {
    // Backspace through the auto-inserted "/" so deleting feels natural.
    if (e.key === "Backspace" && inputValue.endsWith("/") && e.target.selectionStart === inputValue.length) {
      e.preventDefault();
      setInputValue(inputValue.slice(0, -1));
    }
  }

  const totalDays = daysInMonth(viewMonth, viewYear);
  const startDay = firstDayOfMonth(viewMonth, viewYear);
  const weeks = [];
  let dayNum = 1;
  for (let w = 0; w < 6; w++) {
    const week = [];
    for (let d = 0; d < 7; d++) {
      if ((w === 0 && d < startDay) || dayNum > totalDays) {
        week.push(null);
      } else {
        week.push(dayNum++);
      }
    }
    weeks.push(week);
    if (dayNum > totalDays) break;
  }

  const isSelected = (day) =>
    selectedDate && selectedDate.d === day && selectedDate.m === viewMonth && selectedDate.y === viewYear;

  // The hidden input is the contract used by the step's `validate` (reads
  // `card.querySelector('.dob-hidden-value')?.value`). Always keep it in
  // MM/DD/YYYY format so existing validation continues to work.
  const hiddenValue = selectedDate
    ? formatDOB(selectedDate.m, selectedDate.d, selectedDate.y)
    : "";

  return (
    <div className="ob-form-body">
      <input type="hidden" className="dob-hidden-value" value={hiddenValue} readOnly />
      <FieldGroup label="Date of Birth">
        <div className="dob-wrapper" ref={calRef}>
          <div className="dob-input-field">
            <input
              type="text"
              className="dob-input-text"
              inputMode="numeric"
              autoComplete="bday"
              placeholder="MM / DD / YYYY"
              value={inputValue}
              onChange={(e) => {
                handleInputChange(e);
                clearFieldErrorForInput(e);
              }}
              onKeyDown={handleInputKeyDown}
              maxLength={10}
              aria-label="Date of birth, format MM/DD/YYYY"
            />
          <svg
            className="dob-cal-icon"
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            aria-hidden="true"
          >
            <rect x="2" y="3" width="14" height="13" rx="2" stroke="#8B8FA3" strokeWidth="1.3" />
            <path d="M2 7h14" stroke="#8B8FA3" strokeWidth="1.3" />
            <path d="M6 1.5v3M12 1.5v3" stroke="#8B8FA3" strokeWidth="1.3" strokeLinecap="round" />
          </svg>
        </div>

        <div className="dob-calendar">
          <div className="dob-cal-header">
            <button
              className="dob-cal-month-year"
              onClick={() => setShowYearPicker((v) => !v)}
              type="button"
              aria-expanded={showYearPicker}
              aria-label={showYearPicker ? "Close year picker" : "Open year picker"}
            >
              <span className="dob-cal-title">
                {DOB_MONTHS[viewMonth]} {viewYear}
              </span>
              <svg
                className={`dob-cal-caret${showYearPicker ? " open" : ""}`}
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                aria-hidden="true"
              >
                <path d="M3 4.5L6 7.5L9 4.5" stroke="#8B8FA3" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <div className="dob-cal-nav-group">
              <button
                className="dob-cal-nav"
                onClick={showYearPicker ? prevYear : prevMonth}
                type="button"
                aria-label={showYearPicker ? "Previous year" : "Previous month"}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <path d="M12 5L7 10L12 15" stroke="#8B8FA3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <button
                className="dob-cal-nav"
                onClick={showYearPicker ? nextYear : nextMonth}
                type="button"
                aria-label={showYearPicker ? "Next year" : "Next month"}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <path d="M8 5L13 10L8 15" stroke="#8B8FA3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          </div>

          {showYearPicker ? (
            <div className="dob-year-grid" ref={yearGridRef}>
              {years.map((y) => (
                <button
                  key={y}
                  type="button"
                  className={`dob-year-btn${y === viewYear ? " active" : ""}`}
                  onClick={() => selectYear(y)}
                >
                  {y}
                </button>
              ))}
            </div>
          ) : (
            <>
              <div className="dob-cal-days-header">
                {DOB_DAYS.map((d, i) => (
                  <span key={i} className="dob-day-label">{d}</span>
                ))}
              </div>
              <div className="dob-cal-grid">
                {weeks.map((week, wi) => (
                  <div key={wi} className="dob-cal-week">
                    {week.map((day, di) => (
                      <button
                        key={di}
                        type="button"
                        className={`dob-cal-day${day ? "" : " empty"}${isSelected(day) ? " selected" : ""}`}
                        onClick={() => day && selectDay(day)}
                        disabled={!day}
                        aria-label={day ? `${DOB_MONTHS[viewMonth]} ${day}, ${viewYear}` : undefined}
                        aria-pressed={isSelected(day) || undefined}
                      >
                        {day || ""}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
      </FieldGroup>
    </div>
  );
}

/* ═══════════════════════════════════════════
   LOCK INTERSTITIAL
   ═══════════════════════════════════════════ */
function LockInterstitial() {
  return (
    <div className="ob-lock-wrap">
      <div className="ob-lock-icon-wrap">
        <svg className="ob-lock-ring" viewBox="0 0 80 80">
          <circle className="ring-bg" cx="40" cy="40" r="36" />
          <circle className="ring-fg" cx="40" cy="40" r="36" />
        </svg>
        <div className="ob-lock-inner">
          <svg width="52" height="52" viewBox="0 0 40 40" fill="none">
            <rect
              className="lock-body"
              x="11"
              y="18"
              width="18"
              height="14"
              rx="3.5"
              stroke="#54FF99"
              strokeWidth="1.8"
              fill="none"
            />
            <path
              className="lock-shackle"
              d="M14.5 18V14.5C14.5 11.46 16.96 9 20 9C23.04 9 25.5 11.46 25.5 14.5V18"
              stroke="#54FF99"
              strokeWidth="1.8"
              strokeLinecap="round"
              fill="none"
            />
            <circle
              className="lock-keyhole"
              cx="20"
              cy="25"
              r="1.8"
              fill="#54FF99"
            />
          </svg>
        </div>
      </div>
      <div className="ob-lock-text">
        Securing your account with
        <br />
        256-bit encryption...
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   TERMS OF SERVICE — E-SIGN PDF + AGREEMENTS
   ═══════════════════════════════════════════ */
const getSecondaryAgreementLabels = (isSyncteraUser) => [
  <>
    I agree to Stake&rsquo;s{" "}
    <a
      href="https://getyourstake.com/legal/end-user-terms"
      target="_blank"
      rel="noreferrer"
      className="terms-link"
    >
      End User Terms of Service
    </a>
    ,{" "}
    and{" "}
    <a
      href="https://getyourstake.com/legal/privacy-policy"
      target="_blank"
      rel="noreferrer"
      className="terms-link"
    >
      Privacy Policy
    </a>
    .
  </>,
  <>
    I agree to the{" "}
    <a
      href="https://stake-platform-s3.s3.us-east-1.amazonaws.com/terms/consumer-checking-account-agreement.pdf"
      target="_blank"
      rel="noreferrer"
      className="terms-link"
    >
      Consumer Deposit Account Agreement
    </a>
    {isSyncteraUser && (
      <>
        {" "}and the{" "}
        <a
          href="https://www.midlandsb.com/privacy-center/consumer-privacy-notice"
          target="_blank"
          rel="noreferrer"
          className="terms-link"
        >
          Midland U.S. Consumer Privacy Notice
        </a>
      </>
    )}
    .
  </>,
];

function TermsBody({ onTermsChange, isSyncteraUser }) {
  const secondaryLabels = useMemo(() => getSecondaryAgreementLabels(isSyncteraUser), [isSyncteraUser]);
  const [enableEsign, setEnableEsign] = useState(false);
  const [eSignChecked, setESignChecked] = useState(false);
  const [secondaryChecks, setSecondaryChecks] = useState(() => secondaryLabels.map(() => false));

  useEffect(() => {
    setSecondaryChecks(secondaryLabels.map(() => false));
    setESignChecked(false);
    setEnableEsign(false);
  }, [secondaryLabels]);

  const allAgreed = eSignChecked && secondaryChecks.every(Boolean);

  useEffect(() => {
    onTermsChange?.(allAgreed);
  }, [allAgreed, onTermsChange]);

  const handleEsignPageChange = (e) => {
    if (e.currentPage === e.doc.numPages - 1) {
      setEnableEsign(true);
    }
  };

  const handleESignChange = (_event, checked) => {
    if (!enableEsign) return;
    setESignChecked(checked);
    if (!checked) {
      setSecondaryChecks(secondaryLabels.map(() => false));
    }
  };

  const handleSecondaryChange = (index, checked) => {
    if (!eSignChecked) return;
    setSecondaryChecks((prev) => {
      const next = [...prev];
      next[index] = checked;
      return next;
    });
  };

  return (
    <div className="d-flex flex-column row-gap-4">
      <div className="d-flex flex-column row-gap-2">
        <label className="text-sm-medium color--mist mb-0">E-Sign Consent Disclosure</label>
        <p className="text-sm-regular color--american-silver mb-0">
          Please review the E-Sign Consent Disclosure below. You&rsquo;ll need to scroll to the bottom before you can
          continue.
        </p>
      </div>
      <EsignDisclosureViewer onPageChange={handleEsignPageChange} />
      <div className="d-flex flex-column row-gap-1">
        <div className="d-flex align-items-start gap-2">
          <CheckBox
            checked={eSignChecked}
            disabled={!enableEsign}
            onChange={handleESignChange}
            ariaLabelledBy="ob-esign-label"
          />
          <p className="text-base-regular color--mist mb-0" id="ob-esign-label">
            I agree to receive disclosures and communications electronically.
          </p>
        </div>
        <div
          className={`d-flex flex-column row-gap-1 disclosures-secondary${eSignChecked ? "" : " disclosures-secondary--locked"}`}
        >
          {secondaryLabels.map((label, i) => (
            <div key={i} className="d-flex align-items-start gap-2">
              <CheckBox
                checked={secondaryChecks[i]}
                disabled={!eSignChecked}
                onChange={(_event, checked) => handleSecondaryChange(i, checked)}
                ariaLabelledBy={`ob-agreement-label-${i}`}
              />
              <p
                className="text-base-regular color--mist mb-0"
                id={`ob-agreement-label-${i}`}
                onClick={(e) => {
                  if (e.target.closest("a")) return;
                  if (!eSignChecked) return;
                  handleSecondaryChange(i, !secondaryChecks[i]);
                }}
                style={eSignChecked ? { cursor: "pointer" } : undefined}
              >
                {label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   INDUSTRY OPTIONS
   ═══════════════════════════════════════════ */
const INDUSTRY_OPTIONS = [
  "Accommodation and Food Services",
  "Administrative and Support Services",
  "Agriculture, Forestry, Fishing and Hunting",
  "Arts, Entertainment, and Recreation",
  "Construction",
  "Educational Services",
  "Finance and Insurance",
  "Government and Public Administration",
  "Health Care and Social Assistance",
  "Information and Technology",
  "Legal Services",
  "Management of Companies and Enterprises",
  "Manufacturing",
  "Military",
  "Mining, Quarrying, and Oil and Gas Extraction",
  "Professional, Scientific, and Technical Services",
  "Real Estate and Rental and Leasing",
  "Religious, Grantmaking, Civic, Professional Organizations",
  "Retail Trade",
  "Self-Employed / Freelance",
  "Student",
  "Transportation and Warehousing",
  "Unemployed",
  "Utilities",
  "Wholesale Trade",
  "Retired",
  "Other",
];

/* ═══════════════════════════════════════════
   ADDRESS STEP BODY (controlled)
   ═══════════════════════════════════════════ */
function AddressStepBody({ userData, isUmfUser = false }) {
  const [street, setStreet] = useState(userData.address1 || "");
  const [apt, setApt] = useState(userData.address2 || "");
  const [zip, setZip] = useState(userData.zipCode || "");
  const [city, setCity] = useState(userData.city || "");
  const [state, setState] = useState(userData.state || "");

  const handleAddressSelect = (selectedAddress) => {
    setStreet(selectedAddress.address || "");
    setCity(selectedAddress.city || "");
    setState(selectedAddress.state || "");
    setZip(selectedAddress.zipCode || "");
  };

  return (
    <div className="ob-form-body">
      <input type="hidden" className="ob-street-address-value" value={street} />
      <FieldGroup label="Street Address">
        <AddressAutoCompleteField
          fieldProps={{
            label: "Street Address",
            placeholder: "Enter a location",
            className: "filled",
            value: street,
            helperText: "PO Boxes are not accepted",
            onChange: (e) => {
              setStreet(e.target.value);
              clearFieldErrorForInput(e);
            },
          }}
          onAddressSelect={handleAddressSelect}
        />
      </FieldGroup>
      <FieldGroup label="Apt / Suite (optional)">
        <ObInput
          label="Apt / Suite (optional)"
          value={apt}
          onInput={(e) => {
            setApt(e.target.value);
            clearFieldErrorForInput(e);
          }}
        />
      </FieldGroup>
      <FieldGroup label="ZIP Code">
        <ObInput
          label="ZIP Code"
          value={zip}
          onInput={(e) => {
            setZip(e.target.value);
            clearFieldErrorForInput(e);
          }}
        />
      </FieldGroup>
      <div className="ob-input-row">
        <FieldGroup label="City">
          <ObInput
            label="City"
            value={city}
            onInput={(e) => {
              setCity(e.target.value);
              clearFieldErrorForInput(e);
            }}
          />
        </FieldGroup>
        <FieldGroup label="State">
          <ObInput
            label="State"
            value={state}
            onInput={(e) => {
              setState(e.target.value);
              clearFieldErrorForInput(e);
            }}
          />
        </FieldGroup>
      </div>
      {isUmfUser ? (
        <div className="ob-address-moved-callout">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
            <circle cx="8" cy="8" r="6.6" stroke="#A0A4B8" strokeWidth="1.3" />
            <path d="M8 7.2v3.4" stroke="#A0A4B8" strokeWidth="1.3" strokeLinecap="round" />
            <circle cx="8" cy="5.2" r="0.85" fill="#A0A4B8" />
          </svg>
          <div className="ob-address-moved-callout-text">
            <span className="ob-address-moved-callout-title">Recently moved?</span>
            <span className="ob-address-moved-callout-body">
              Add your previous address so we can match it to your records and verify your identity faster.
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ═══════════════════════════════════════════
   UMF ADDRESS CONFIRMATION OVERLAY
   ═══════════════════════════════════════════ */
function AddressConfirmationOverlay({ onEditAddress, onKeepAddress, isSaving }) {
  return (
    <div className="ob-address-confirm-overlay" role="dialog" aria-modal="true" aria-labelledby="ob-address-confirm-title">
      <div className="ob-address-confirm-dialog">
        <div className="ob-address-confirm-icon-wrap" aria-hidden>
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 21s7-5.686 7-11a7 7 0 1 0-14 0c0 5.314 7 11 7 11Z"
              stroke="#54FF99"
              strokeWidth="1.6"
              strokeLinejoin="round"
            />
            <circle cx="12" cy="10" r="2.5" fill="none" stroke="#54FF99" strokeWidth="1.6" />
          </svg>
        </div>
        <div className="ob-address-confirm-copy">
          <h2 id="ob-address-confirm-title" className="ob-address-confirm-title">
            Is this your residential address?
          </h2>
          <p className="ob-address-confirm-subtitle">
            This looks like a new address. Please enter the residential address on your records so we can verify your identity.
          </p>
        </div>
        <div className="ob-address-confirm-actions">
          <button
            type="button"
            className="ob-address-confirm-btn-primary"
            onClick={onEditAddress}
            disabled={isSaving}
          >
            Edit address
          </button>
          <button
            type="button"
            className={`ob-address-confirm-btn-secondary${isSaving ? " is-loading" : ""}`}
            onClick={onKeepAddress}
            disabled={isSaving}
          >
            Keep this address
            {isSaving && <CircularProgress size={18} thickness={5} className="ob-address-confirm-btn-spinner" />}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   STEP DEFINITIONS — 10-step wizard
   ═══════════════════════════════════════════ */
function buildSteps(
  onNext,
  onTermsChange,
  userData,
  {
    balance = 0,
    annualCashback = 120,
    onContactSupport,
    isSyncteraUser = false,
    isStatusApproved = false,
    isStatusEligible = false,
    isBankConnected = false,
    stakeDebitData = null,
    isBattlecardFlow = false,
    includeCongrats = true,
    isUmfUser = false,
    hasVirtualCard = false,
    hasPhysicalCard = false,
    isVirtualCardPinSet = false,
    pinJustSet = false,
    hasPendingTransaction = false,
    justGotVirtualCard = false,
    virtualCardId = null,
    setPinStyles,
    onPinSetSuccess,
  } = {},
) {
  const u = userData || {};
  const prefillPhone = u.phoneNumber
    ? transformCountryCodeFormatToUSNumber(u.phoneNumber)
    : "";
  const ssnDigits = (u.ssn || "").replace(/\D/g, "");
  const formattedSsn = ssnDigits.length === 9
    ? `${ssnDigits.slice(0, 3)}-${ssnDigits.slice(3, 5)}-${ssnDigits.slice(5, 9)}`
    : "";

  return [
    // Step 1 — Name
    {
      progress: 0.05,
      title: "First, the basics.",
      subtitle: u.firstName
        ? "We filled in what we know. Check it over and fix what's off."
        : "Enter your legal name as it appears on your ID.",
      btn: "Next",
      validate: (card) => {
        const body = card.querySelector(".ob-form-body");
        const first = getInputByLabel(card, "First Name")?.value || "";
        const last = getInputByLabel(card, "Last Name")?.value || "";
        const errors = [];

        if (!first.trim()) {
          errors.push({ label: "First Name", message: "This field is required" });
        } else if (!validation.name.hasAtleast2Characters(first)) {
          errors.push({ label: "First Name", message: "First name must be at least 2 characters" });
        } else if (!validation.name.isAllowedCharacters(first)) {
          errors.push({ label: "First Name", message: "First name contains invalid characters" });
        } else if (!validation.name.hasAtmost50Characters(first)) {
          errors.push({ label: "First Name", message: "First name must not exceed 50 characters" });
        }

        if (!last.trim()) {
          errors.push({ label: "Last Name", message: "This field is required" });
        } else if (!validation.name.isAllowedCharacters(last)) {
          errors.push({ label: "Last Name", message: "Last name contains invalid characters" });
        } else if (!validation.name.hasAtmost50Characters(last)) {
          errors.push({ label: "Last Name", message: "Last name must not exceed 50 characters" });
        }

        if (errors.length) {
          applyFieldErrors(body, errors);
          return false;
        }
        clearAllFieldErrors(body);
        return true;
      },
      body: (
        <div className="ob-form-body">
          <div className="ob-input-row">
            <FieldGroup label="First Name">
              <ObInput
                label="First Name"
                defaultValue={u.firstName || ""}
                onInput={clearFieldErrorForInput}
              />
            </FieldGroup>
            <FieldGroup label="Last Name">
              <ObInput
                label="Last Name"
                defaultValue={u.lastName || ""}
                onInput={clearFieldErrorForInput}
              />
            </FieldGroup>
          </div>
          <FieldGroup label="Middle Name (optional)">
            <ObInput label="Middle Name (optional)" defaultValue={u.middleName || ""} />
          </FieldGroup>
          <ObInput label="Email" value={u.email} disabled />
          <button type="button" className="ob-contact-support" onClick={onContactSupport}>
            Need to change your email? Contact our member support.
          </button>
        </div>
      ),
    },
    // Step 2 — DOB
    {
      progress: 0.1,
      title: "Your date of birth",
      subtitle: "You must be 18 or older to have a Stake account.",
      btn: "Next",
      validate: (card) => {
        const body = card.querySelector(".ob-form-body");
        const raw = card.querySelector(".dob-hidden-value")?.value || "";
        const errors = [];

        if (!raw) {
          errors.push({ label: "Date of Birth", message: "This field is required" });
        } else {
          const [mm, dd, yyyy] = raw.split("/").map(Number);
          const dob = new Date(yyyy, mm - 1, dd);
          const today = new Date();
          let age = today.getFullYear() - dob.getFullYear();
          const monthDiff = today.getMonth() - dob.getMonth();
          if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate()))
            age--;
          if (age < 18) {
            errors.push({ label: "Date of Birth", message: "You must be at least 18 years old" });
          }
        }

        if (errors.length) {
          applyFieldErrors(body, errors);
          return false;
        }
        clearAllFieldErrors(body);
        return true;
      },
      body: <DOBCalendar initialDate={u.birthdate} />,
    },
    // Step 3 — Phone
    {
      progress: 0.2,
      title: "Phone number",
      subtitle: "Confirm it or swap it out.",
      btn: "Next",
      validate: (card) => {
        const body = card.querySelector(".ob-form-body");
        const phone = getInputByLabel(card, "Phone Number")?.value || "";
        if (!validation.number.is10DigitPhoneNumber(phone)) {
          applyFieldErrors(body, [
            { label: "Phone Number", message: "Your phone number should be 10 digits long" },
          ]);
          return false;
        }
        clearAllFieldErrors(body);
        return true;
      },
      body: (
        <div className="ob-form-body">
          <FieldGroup label="Phone Number">
            <ObInput
              label="Phone Number"
              placeholder="(xxx) xxx-xxxx"
              value={prefillPhone}
              disabled
            />
          </FieldGroup>
          <button type="button" className="ob-contact-support" onClick={onContactSupport}>
            Need to change your phone number? Contact our member support.
          </button>
        </div>
      ),
    },
    // Step 4 — Address
    {
      progress: 0.3,
      title: "Home address",
      subtitle: "Same deal. Check it over and fix what's off.",
      btn: "Next",
      validate: (card) => {
        const body = card.querySelector(".ob-form-body");
        const street = card.querySelector(".ob-street-address-value")?.value || "";
        const zip = getInputByLabel(card, "ZIP Code")?.value || "";
        const city = getInputByLabel(card, "City")?.value || "";
        const state = getInputByLabel(card, "State")?.value || "";
        const errors = [];

        const streetResult =
          validation.streetAddress.hasAtleast3Characters(street);
        if (streetResult !== true) {
          errors.push({
            label: "Street Address",
            message:
              typeof streetResult === "string"
                ? streetResult
                : "Your address must be at least 3 characters long",
          });
        }
        if (!zip.trim()) {
          errors.push({ label: "ZIP Code", message: "This field is required" });
        } else {
          const zipResult = validation.zipcode.is5DigitZipCode(zip);
          if (zipResult !== true) {
            errors.push({
              label: "ZIP Code",
              message:
                typeof zipResult === "string"
                  ? zipResult
                  : "Please enter a valid ZIP code",
            });
          }
        }
        const cityResult = validation.city.hasAtleast2Characters(city);
        if (cityResult !== true) {
          errors.push({
            label: "City",
            message:
              typeof cityResult === "string"
                ? cityResult
                : "Your city must be at least 2 characters long",
          });
        }
        if (!state.trim()) {
          errors.push({ label: "State", message: "This field is required" });
        }

        if (errors.length) {
          applyFieldErrors(body, errors);
          return false;
        }
        clearAllFieldErrors(body);
        return true;
      },
      body: <AddressStepBody userData={u} isUmfUser={isUmfUser} />,
    },
    // Step 5 — Lock Interstitial
    {
      progress: 0.4,
      interstitial: true,
      noBack: true,
      custom: <LockInterstitial />,
    },
    // Step 6 — Verify Identity (SSN)
    {
      progress: 0.5,
      title: "Verify it's really you",
      subtitle:
        "We confirm your identity to protect your account. This\nhas no effect on your credit score.",
      btn: "Next",
      validate: (card) => {
        const body = card.querySelector(".ob-form-body");
        const ssn =
          getInputByLabel(card, "Social Security Number")?.value || "";
        const confirm = getInputByLabel(card, "Confirm SSN")?.value || "";
        const digits = ssn.replace(/\D/g, "");
        const errors = [];

        if (digits.length !== 9) {
          errors.push({
            label: "Social Security Number",
            message: "Please enter a valid 9-digit Social Security Number",
          });
        }
        if (!confirm.trim()) {
          errors.push({ label: "Confirm SSN", message: "This field is required" });
        } else if (ssn !== confirm) {
          errors.push({
            label: "Confirm SSN",
            message: "Social Security Numbers do not match",
          });
        }

        if (errors.length) {
          applyFieldErrors(body, errors);
          return false;
        }
        clearAllFieldErrors(body);
        return true;
      },
      body: (
        <div className="ob-form-body">
          <FieldGroup label="Social Security Number">
            <SSNInput
              label="Social Security Number"
              placeholder="xxx-xx-xxxx"
              maxLength={11}
              ssn={u.ssn}
            />
          </FieldGroup>
          <FieldGroup label="Confirm SSN">
            <ObInput
              label="Confirm SSN"
              placeholder="xxx-xx-xxxx"
              defaultValue={formattedSsn}
              onInput={(e) => {
                formatSSN(e);
                clearFieldErrorForInput(e);
              }}
              maxLength={11}
            />
          </FieldGroup>
          <div className="ob-encryption">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M8 1.33L3.33 3.33V7.33C3.33 10.47 5.27 13.4 8 14.33C10.73 13.4 12.67 10.47 12.67 7.33V3.33L8 1.33Z"
                stroke="#54FF99"
                strokeWidth="1.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M5.67 8L7.33 9.67L10.33 6.67"
                stroke="#54FF99"
                strokeWidth="1.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Your info is secured with 256-bit encryption
          </div>
        </div>
      ),
    },
    // Step 7 — Almost Done (employment)
    {
      progress: 0.6,
      title: "Almost through",
      subtitle: "A few final details and your account is set.",
      btn: "Next",
      validate: (card) => {
        const body = card.querySelector(".ob-form-body");
        const employer = getInputByLabel(card, "Employer Name")?.value || "";
        const occupation = getInputByLabel(card, "Occupation")?.value || "";
        const industry = getInputByLabel(card, "Industry")?.value || "";
        const deposits =
          getInputByLabel(card, "Monthly Deposits*")?.value;
        const withdrawals =
          getInputByLabel(card, "Monthly Withdrawals*")?.value;
        const errors = [];

        if (!employer.trim()) {
          errors.push({ label: "Employer Name", message: "This field is required" });
        } else if (employer.trim().length < 2) {
          errors.push({
            label: "Employer Name",
            message: "Employer name must be at least 2 characters",
          });
        }
        if (!occupation) {
          errors.push({ label: "Occupation", message: "This field is required" });
        }
        if (!industry) {
          errors.push({ label: "Industry", message: "This field is required" });
        }
        if (!deposits) {
          errors.push({
            label: "Monthly Deposits*",
            message: "This field is required",
          });
        }
        if (!withdrawals) {
          errors.push({
            label: "Monthly Withdrawals*",
            message: "This field is required",
          });
        }

        if (errors.length) {
          applyFieldErrors(body, errors);
          return false;
        }
        clearAllFieldErrors(body);
        return true;
      },
      body: (
        <div className="ob-form-body">
          <ObSelect
            label="Citizenship"
            defaultValue={u.citizenship || ""}
            hasValue
            onChange={handleCitizenshipChange}
          >
            {CountriesList.map((c) => (
              <MenuItem key={c.value} value={c.label}>
                {c.label}
              </MenuItem>
            ))}
          </ObSelect>
          <div className="ob-error-text"></div>
          <FieldGroup label="Employer Name">
            <ObInput
              label="Employer Name"
              defaultValue={u.employerName || ""}
              onInput={clearFieldErrorForInput}
            />
          </FieldGroup>
          <FieldGroup label="Occupation">
            <ObSelect
              label="Occupation"
              defaultValue={u.occupation || ""}
              onChange={(e) => {
                clearFieldErrorForInput(e);
              }}
            >
              {UnitOccupationList.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </ObSelect>
          </FieldGroup>
          <FieldGroup label="Industry">
            <ObSelect
              label="Industry"
              defaultValue={u.industry || ""}
              onChange={(e) => {
                clearFieldErrorForInput(e);
              }}
            >
              {INDUSTRY_OPTIONS.map((o) => (
                <MenuItem key={o} value={o}>
                  {o}
                </MenuItem>
              ))}
            </ObSelect>
          </FieldGroup>
          <div className="ob-input-row">
            <FieldGroup label="Monthly Deposits*">
              <ObInput
                label="Monthly Deposits*"
                prefix="$"
                type="number"
                defaultValue={u.expectedMonthlyDeposit}
                onInput={clearFieldErrorForInput}
              />
            </FieldGroup>
            <FieldGroup label="Monthly Withdrawals*">
              <ObInput
                label="Monthly Withdrawals*"
                prefix="$"
                type="number"
                defaultValue={u.expectedMonthlyWithdrawal}
                onInput={clearFieldErrorForInput}
              />
            </FieldGroup>
          </div>
          <div className="ob-help-text">
            * We've set these to 2x your rent. Adjust if you expect different
            usage.
          </div>
        </div>
      ),
    },
    // Step 8 — Terms
    {
      progress: 0.7,
      title: "Review & agree",
      subtitle:
        "One last step. Read the terms below to finish creating your account.",
      btn: "Agree & continue",
      requiresTerms: true,
      body: <TermsBody onTermsChange={onTermsChange} isSyncteraUser={isSyncteraUser} />,
    },
    // Post-apply: review pending / card sequence / add cash / connect bank
    ...(() => {
      const postApplyStep = buildPostApplyStep({
        isStatusApproved,
        isStatusEligible,
        balance,
        isBankConnected,
        stakeDebitData,
        isBattlecardFlow,
        hasVirtualCard,
        hasPhysicalCard,
        hasPendingTransaction,
        isVirtualCardPinSet,
        justGotVirtualCard,
        virtualCardId,
        setPinStyles,
        onPinSetSuccess,
      });
      return postApplyStep ? [postApplyStep] : [];
    })(),
    ...(includeCongrats
      ? [buildCongratsStep({ hasVirtualCard, isVirtualCardPinSet, pinJustSet })]
      : []),
  ];
}

// The final step doubles as a nudge to set a PIN when the user has a virtual card but
// hasn't set one yet (and hasn't just set one in this session) — skippable, since a PIN
// isn't required to use the card and can always be set later from Manage Cards.
function buildCongratsStep({ hasVirtualCard = false, isVirtualCardPinSet = false, pinJustSet = false } = {}) {
  if (hasVirtualCard && !isVirtualCardPinSet && !pinJustSet) {
    return {
      progress: 0.9,
      noBack: true,
      congrats: true,
      pinPromptVariant: true,
      btn: "Set PIN",
      btn2: "Skip",
      setPinNowAction: true,
      custom: (
        <div style={{ textAlign: "center" }}>
          <div className="sc-ob-title" style={{ textAlign: "center", marginTop: 32 }}>
            Activated. Now lock it in.
          </div>
          <div className="sc-ob-subtitle" style={{ textAlign: "center" }}>
            Your card is active. Now, set a PIN so purchases at stores and ATMs that require
            one go through. Without a PIN, those transactions can be declined.
          </div>
        </div>
      ),
    };
  }

  return {
    progress: 0.9,
    noBack: true,
    congrats: true,
    btn: "Finish",
    custom: (
      <div style={{ textAlign: "center" }}>
        <div className="ob-center-icon">
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
            <circle cx="24" cy="24" r="20" stroke="#54FF99" strokeWidth="2" />
            <path d="M16 24l6 6 10-10" stroke="#54FF99" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <div className="ob-congrats-title">You're in.</div>
        <div className="ob-congrats-body">
          Your debit card has been requested!<br></br>You're ready to start earning cash back.
        </div>
      </div>
    ),
  };
}

// Returns title + description + CTA for the review-pending branch based on the
// specific stake_debit status (pending, under review, verification required,
// denied, ineligible, closed for non-compliance, or unknown fallback).
function getReviewPendingContent(stakeDebitData) {
  const svd = stakeDebitData?.data;
  if (isSVDPendingVerification(svd) || isSVDVerificationPendingStatus(svd)) {
    return {
      title: "We need a few more details",
      description:
        "Confirming your identity took an extra step this time. This will wrap it up.",
      btn: "Verify",
      reviewAction: "verify",
    };
  }
  if (isSVDDenied(svd)) {
    return {
      title: "We couldn't approve your application",
      description:
        "We're sorry, but we can't approve your Stake Checking application at this time. If you have questions, contact Support.",
      btn: "Contact Support",
      reviewAction: "support",
    };
  }
  if (isSVDIneligible(svd)) {
    return {
      title: "You're not eligible",
      description:
        "You're not eligible for a Stake Checking account at this time.",
      btn: "Okay",
      reviewAction: "close",
    };
  }
  if (isSVDClosedForStakeSaverNonCompliance(svd)) {
    return {
      title: "Stake Checking is unavailable",
      description:
        "Your Stake Checking account was closed due to non-compliance. You still have full access to your Stake Wallet and rewards.",
      btn: "Contact Support",
      reviewAction: "support",
    };
  }
  if (isSVDReview(svd)) {
    return {
      title: "Your application is under review",
      description:
        "We're reviewing your application now. This usually takes a short time. We'll email and text you as soon as there's an update.",
      btn: "Okay",
      reviewAction: "close",
    };
  }
  if (isSVDPending(svd) && !isSVDVerificationPendingStatus(svd)) {
    return {
      title: "Your application is pending",
      description:
        "We've received your application and it's currently pending. We'll email and text you as soon as there's an update.",
      btn: "Okay",
      reviewAction: "close",
    };
  }
  return {
    title: "Your application is under review",
    description:
      "This can take anywhere from a few minutes to a few business days. We'll notify you once it's approved.",
    btn: "Okay",
    reviewAction: "close",
  };
}

// Resolves the post-apply branch step (index 8) based on current stake_debit status,
// available balance, and whether the user has any connected bank accounts.
//
// The card sequence (request virtual → set PIN → request physical) shares this one slot:
// each step refetches stake_debit, so the slot rebuilds into the next one in place.
function buildPostApplyStep({
  isStatusApproved,
  isStatusEligible,
  balance,
  isBankConnected,
  stakeDebitData,
  isBattlecardFlow = false,
  hasVirtualCard = false,
  hasPhysicalCard = false,
  hasPendingTransaction = false,
  isVirtualCardPinSet = false,
  justGotVirtualCard = false,
  virtualCardId = null,
  setPinStyles,
  onPinSetSuccess,
}) {
  if (!isStatusApproved && !isStatusEligible) {
    const { title, description, btn, reviewAction } = getReviewPendingContent(stakeDebitData);
    return {
      progress: 0.8,
      noBack: true,
      reviewPending: true,
      reviewAction,
      btn,
      custom: (
        <div style={{ textAlign: "center" }}>
          <div className="sc-ob-title" style={{ textAlign: "center", marginTop: 32 }}>
            {title}
          </div>
          <div className="sc-ob-subtitle" style={{ textAlign: "center" }}>
            {description}
          </div>
        </div>
      ),
    };
  }
  if (isBattlecardFlow) {
    return null;
  }
  // Shown once, right when the virtual card is newly issued — whether the user just
  // requested it or the webhook auto-issued it in the background — not on every future
  // visit to the wizard; a persistent nudge for that lives on the final congrats step.
  // The widget is rendered inline so entering the PIN is a single step, no separate
  // "Set PIN" button/modal hop needed — "Skip for now" sits right alongside it.
  if (hasVirtualCard && !isVirtualCardPinSet && justGotVirtualCard) {
    return {
      progress: 0.8,
      noBack: true,
      btn2: "Skip for now",
      dismissPinGate: true,
      custom: (
        <div style={{ textAlign: "center" }}>
          <div className="sc-ob-title" style={{ textAlign: "center", marginTop: 32 }}>
            Activated. Now lock it in.
          </div>
          <div className="sc-ob-subtitle" style={{ textAlign: "center" }}>
            Your card is active. Now, set a PIN so purchases at stores and ATMs that require
            one go through. Without a PIN, those transactions can be declined.
          </div>
          <SetPinModal
            cardId={virtualCardId}
            styles={setPinStyles}
            embedded
            onSuccess={onPinSetSuccess}
          />
        </div>
      ),
    };
  }
  if (hasVirtualCard && !hasPhysicalCard) {
    return {
      progress: 0.8,
      noBack: true,
      btn: "Request my card",
      btn2: "Skip for now",
      skipToExit: true,
      requestCardAction: "PHYSICAL",
      custom: (
        <div style={{ textAlign: "center" }}>
          <div className="sc-ob-title" style={{ textAlign: "center", marginTop: 32 }}>
            Want one for your wallet?
          </div>
          <div className="sc-ob-subtitle" style={{ textAlign: "center" }}>
            Your virtual card is good to go. Want a physical one too? We'll mail it at no extra
            charge.
          </div>
          <img
            src={stakeDebitImg}
            alt="Stake Debit Card"
            className="ob-visa-card-img"
          />
        </div>
      ),
    };
  }
  // The onError handler for this action already covers the case where the webhook wins
  // the race (a 409 that turns out to mean the card landed), so there's no need to make
  // the user wait through a spinner first — just offer the request right away.
  if (balance > 0 && !hasVirtualCard) {
    return {
      progress: 0.8,
      noBack: true,
      btn: "Request card",
      btn2: "Skip for now",
      skipToExit: true,
      requestCardAction: "VIRTUAL",
      custom: (
        <div style={{ textAlign: "center" }}>
          <div className="sc-ob-title" style={{ textAlign: "center", marginTop: 32 }}>
            Request your virtual card
          </div>
          <div className="sc-ob-subtitle" style={{ textAlign: "center" }}>
            Your account is funded. Request your card to start spending.
          </div>
          <img
            src={stakeDebitImg}
            alt="Stake Debit Card"
            className="ob-visa-card-img"
          />
        </div>
      ),
    };
  }
  // Funded and holding both cards — nothing left to prompt for, go straight to congrats.
  if (balance > 0) {
    return null;
  }
  // Cash is already on its way — prompting to "Add Cash" again here would be confusing,
  // so wait for the pending transfer to clear instead.
  if (hasPendingTransaction) {
    return {
      progress: 0.8,
      noBack: true,
      btn2: "Close",
      skipToExit: true,
      custom: (
        <div style={{ textAlign: "center" }}>
          <div className="ob-card-setup-icon">
            <CircularProgress size={32} thickness={4} />
          </div>
          <div className="sc-ob-title" style={{ textAlign: "center" }}>
            Transfer pending…
          </div>
          <div className="sc-ob-subtitle" style={{ textAlign: "center" }}>
            Once your pending transfer clears, you can request your card.
          </div>
        </div>
      ),
    };
  }
  if (isBankConnected) {
    return {
      progress: 0.8,
      noBack: true,
      addCashAction: true,
      btn: "Add Cash",
      btn2: "Skip for now",
      skipToFinish: true,
      custom: (
        <div style={{ textAlign: "center" }}>
          <div className="sc-ob-title" style={{ textAlign: "center", marginTop: 32 }}>
            Add Cash
          </div>
          <div className="sc-ob-subtitle" style={{ textAlign: "center" }}>
            Add any amount to your account to activate your card.
          </div>
        </div>
      ),
    };
  }
  return {
    progress: 0.8,
    noBack: true,
    connectBankAction: true,
    btn: "Connect Bank",
    btn2: "Skip for now",
    skipToFinish: true,
    custom: (
      <div style={{ textAlign: "center" }}>
        <div className="sc-ob-title" style={{ textAlign: "center", marginTop: 32 }}>
          Connect your bank
        </div>
        <div className="sc-ob-subtitle" style={{ textAlign: "center" }}>
          Link a bank account to add funds and start earning cash back on rent and purchases.
        </div>
        <img
          src={stakeDebitImg}
          alt="Stake Debit Card"
          className="ob-visa-card-img"
        />
      </div>
    ),
  };
}

/* ═══════════════════════════════════════════
   STEP INDEX MAP — maps checking-step keys to wizard index
   ═══════════════════════════════════════════ */
const STEP_KEY_TO_INDEX = {
  "checking-name": 0,
  "checking-dob": 1,
  "checking-phone": 2,
  "checking-address": 3,
  "checking-lock": 4,
  "checking-identity": 5,
  "checking-employment": 6,
  "checking-agreement": 7,
  "checking-card": 8,
  "checking-add-funds": 9,
  "checking-congrats": 10,
};

/* ═══════════════════════════════════════════
   MEMBER SETUP MILESTONE PROGRESS (Paper 8ND-0)
   ═══════════════════════════════════════════ */
const MEMBER_SETUP_MILESTONES = [
  { lines: ["Sign up for", "Stake Visa"], align: "start" },
  { lines: ["Add funds"], align: "center" },
  { lines: ["Make a", "purchase"], align: "end" },
];

/**
 * Paper UMF-Basic signup % (steps 0–3, 5–6; lock interstitial = 50).
 * Mapped to the first track segment only (0–50% of bar) so fill never reaches Add funds until Terms (8V6-0).
 */
const SIGNUP_MILESTONE_PAPER_PERCENT = {
  0: 5,
  1: 10,
  2: 20,
  3: 40,
  4: 50,
  5: 60,
  6: 70,
};

/** Dots sit at 0% / 50% / 100%; signup progress stays left of the center (Add funds) dot. */
const SIGNUP_TRACK_SEGMENT_MAX = 50;

function signupMilestoneFillWidth(paperPercent) {
  const clamped = Math.min(paperPercent, 70);
  const trackPercent = (clamped / 100) * SIGNUP_TRACK_SEGMENT_MAX;
  return `${trackPercent}%`;
}

const SIGNUP_MILESTONE_DEFAULT = {
  activeDots: [true, false, false],
  activeLabels: [true, false, false],
  variant: "standard",
};

/** Paper 76Q-0 — Add Funds hub: milestone 2 reached */
export const ADD_FUNDS_PHASE_MILESTONE = {
  fillWidth: "50%",
  activeDots: [true, true, false],
  activeLabels: [true, true, false],
  variant: "standard",
  labelAccent: true,
};

/** Paper 8YM-0 — Post-signup steps (connect bank, card request, add-cash intro): segmented track */
const POST_SIGNUP_SEGMENTED_MILESTONE = {
  fillWidth: null,
  activeDots: [true, false, false],
  activeLabels: [true, false, false],
  variant: "segmented",
  segmentFillPercent: 92,
};

/** Paper 94S-0 — Transfer confirmed: at Add funds, not past Make a purchase */
export const TRANSFER_CONFIRMED_MILESTONE = ADD_FUNDS_PHASE_MILESTONE;

/** Paper 9EZ-0 — Skip direct deposit: progress stays at Add funds */
export const SKIP_DIRECT_DEPOSIT_MILESTONE = ADD_FUNDS_PHASE_MILESTONE;

function getMemberSetupMilestoneState(step, current) {
  if (current?.requiresTerms) {
    return {
      fillWidth: null,
      activeDots: [true, false, false],
      activeLabels: [true, false, false],
      variant: "segmented",
      segmentFillPercent: 82,
    };
  }

  if (
    current?.connectBankAction ||
    current?.addCashAction ||
    current?.requestCardAction ||
    current?.dismissPinGate
  ) {
    return POST_SIGNUP_SEGMENTED_MILESTONE;
  }

  if (step >= 8) {
    if (current?.reviewPending) {
      return POST_SIGNUP_SEGMENTED_MILESTONE;
    }
    return {
      fillWidth: "60%",
      ...SIGNUP_MILESTONE_DEFAULT,
    };
  }

  const paperPercent = current?.interstitial
    ? SIGNUP_MILESTONE_PAPER_PERCENT[4]
    : SIGNUP_MILESTONE_PAPER_PERCENT[step] ?? 5;

  return {
    fillWidth: signupMilestoneFillWidth(paperPercent),
    ...SIGNUP_MILESTONE_DEFAULT,
  };
}

/** @deprecated Use getMemberSetupMilestoneState — coarse phase for legacy callers */
function getMemberSetupMilestonePhase(step, current) {
  const { activeDots, fillWidth } = getMemberSetupMilestoneState(step, current);
  if (activeDots[2]) return 2;
  if (activeDots[1]) return 1;
  if (fillWidth === "100%") return 2;
  return 0;
}

export function MemberSetupMilestoneProgress({ milestone }) {
  const { fillWidth, activeDots, activeLabels, variant, labelAccent, segmentFillPercent = 82 } = milestone;

  const labelRow = (
    <div className="bc-member-milestone-labels">
      {MEMBER_SETUP_MILESTONES.map((milestoneItem, index) => (
        <div
          key={index}
          className={`bc-member-milestone-label-col ${milestoneItem.align}${activeLabels[index] ? " active" : ""}`}
        >
          {milestoneItem.lines.map((line) => (
            <span key={line} className="bc-member-milestone-label-line">
              {line}
            </span>
          ))}
        </div>
      ))}
    </div>
  );

  if (variant === "segmented") {
    return (
      <div className="bc-member-milestone bc-member-milestone--segmented">
        <div className="bc-member-milestone-segmented-track" aria-hidden>
          <span className="bc-member-milestone-seg-dot active" />
          <span
            className="bc-member-milestone-seg-line bc-member-milestone-seg-line--partial"
            style={{
              background: `linear-gradient(90deg, #54FF99 ${segmentFillPercent}%, #252A3A ${segmentFillPercent}%)`,
            }}
          />
          <span className="bc-member-milestone-seg-dot hollow" />
          <span className="bc-member-milestone-seg-line" />
          <span className="bc-member-milestone-seg-dot hollow" />
        </div>
        {labelRow}
      </div>
    );
  }

  return (
    <div className={`bc-member-milestone${labelAccent ? " bc-member-milestone--add-funds-phase" : ""}`}>
      <div className="bc-member-milestone-track-row">
        <div className="bc-member-milestone-track-bg" />
        <div
          className="bc-member-milestone-track-fill"
          style={{ width: fillWidth }}
        />
        <div className="bc-member-milestone-dots">
          {MEMBER_SETUP_MILESTONES.map((_, index) => (
            <span
              key={index}
              className={`bc-member-milestone-dot${activeDots[index] ? " active" : ""}`}
            />
          ))}
        </div>
      </div>
      {labelRow}
    </div>
  );
}

/* ═══════════════════════════════════════════
   WIZARD SKELETON
   ═══════════════════════════════════════════ */
const skeletonSx = { bgcolor: "rgba(255,255,255,0.06)", borderRadius: "8px" };
const skeletonSxPill = { bgcolor: "rgba(255,255,255,0.06)", borderRadius: "20px" };

function WizardSkeleton({ onClose, variant = "modal", hideMilestone = false }) {
  const skeletonBody = (
    <>
      <div className="sc-ob-step-body" style={{ opacity: 1, gap: 0 }}>
        <Skeleton variant="rounded" width="70%" height={22} sx={{ ...skeletonSxPill, alignSelf: "center", mb: "8px" }} />
        <Skeleton variant="rounded" width="85%" height={14} sx={{ ...skeletonSxPill, alignSelf: "center", mb: "4px" }} />
        <Skeleton variant="rounded" width="75%" height={14} sx={{ ...skeletonSxPill, alignSelf: "center", mb: "24px" }} />
        <Skeleton variant="rounded" width="100%" height={52} sx={{ ...skeletonSx, mb: "16px" }} />
        <Skeleton variant="rounded" width="100%" height={52} sx={{ ...skeletonSx, mb: "16px" }} />
      </div>
      <div className="sc-ob-actions">
        <Skeleton variant="rounded" width="100%" height={48} sx={{ ...skeletonSx, borderRadius: "24px" }} />
      </div>
    </>
  );

  if (variant === "page") {
    return (
      <div className="bc-member-setup-inner">
        <div className="bc-member-setup-header">
          <button type="button" className="bc-member-setup-back" onClick={onClose} aria-label="Go back">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
              <path d="M7.5 2.5L4 6L7.5 9.5" stroke="#8B8FA3" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <span className="bc-member-setup-title">Become a Member</span>
        </div>
        {hideMilestone ? null : (
          <div className="bc-member-milestone">
            <Skeleton variant="rounded" width="100%" height={10} sx={{ bgcolor: "#252A3A", borderRadius: "2px" }} />
          </div>
        )}
        <div className="bc-member-setup-divider" />
        <div className="sc-onboarding-step sc-onboarding-step--page">{skeletonBody}</div>
      </div>
    );
  }

  return (
    <div className="sc-onboarding-step">
      <div className="sc-ob-header">
        <button className="sc-ob-back-btn hidden" type="button" disabled aria-label="Go back">
          <svg viewBox="0 0 10 10" fill="none">
            <path
              d="M7 1L3 5L7 9"
              stroke="#8B8FA3"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <Skeleton variant="rounded" width={140} height={18} sx={skeletonSxPill} />
        <button className="sc-ob-close-btn" type="button" onClick={onClose} aria-label="Close">
          <svg viewBox="0 0 12 12" fill="none">
            <path
              d="M1 1L11 11M11 1L1 11"
              stroke="#8B8FA3"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>
      <div className="sc-ob-progress" style={{ paddingBottom: 14 }}>
        <Skeleton variant="rounded" width="100%" height={4} sx={{ bgcolor: "#252A3A", borderRadius: "2px" }} />
      </div>
      <div className="sc-ob-divider" />
      {skeletonBody}
    </div>
  );
}

/* ═══════════════════════════════════════════
   WIZARD COMPONENT
   ═══════════════════════════════════════════ */
const INDEX_TO_STEP_KEY = Object.fromEntries(
  Object.entries(STEP_KEY_TO_INDEX).map(([k, v]) => [v, k]),
);

export const SC_ONBOARDING_STORAGE_KEY = "sc_onboarding_step";

const VERIFY_BUTTON_LOAD_MIN_MS = 3000;

export function StakeCheckingWizard({
  variant = "modal",
  onExit,
  onComplete,
  onVerificationRejected,
  onDepositAccountCreated,
  hideMilestone = false,
  onApplied,
  onApplyingChange,
} = {}) {
  const modalContext = useModalContext();
  const { setModalStack, popModalStack, onModalClose } = modalContext;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isPageVariant = variant === "page";

  useSyncteraWidgetScripts();
  const setPinStyles = useMemo(() => readSyncteraBrandStyles({ variant: "form" }), []);

  const exitWizard = useCallback(() => {
    if (isPageVariant) {
      onExit?.();
    } else {
      popModalStack(1);
    }
  }, [isPageVariant, onExit, popModalStack]);

  const finishWizard = useCallback(() => {
    if (isPageVariant) {
      if (onComplete) {
        onComplete();
      } else {
        window.location.assign("/home");
      }
    } else {
      popModalStack(1);
    }
  }, [isPageVariant, onComplete, popModalStack]);

  const redirectVerificationRejected = useCallback(() => {
    if (onVerificationRejected) {
      onVerificationRejected();
      return;
    }
    navigate("/battlecard", {
      replace: true,
      state: { ...BATTLECARD_LEGACY_ONLY_STATE, ...BATTLECARD_FLOW_NAV_STATE },
    });
  }, [navigate, onVerificationRejected]);

  const redirectDepositAccountCreated = useCallback(() => {
    if (onDepositAccountCreated) {
      onDepositAccountCreated();
      return;
    }
    navigate("/battlecard/add-funds", { replace: true, state: BATTLECARD_FLOW_NAV_STATE });
  }, [navigate, onDepositAccountCreated]);
  const { show: showIntercom } = useIntercom();
  const queryClient = useQueryClient();
  const { mutate: saveProgressState, isLoading: isSavingProgress } = useSaveStakeCheckingProgressState();
  const { mutate: applyStakeChecking, isLoading: isApplying } = useApplyStakeChecking();
  const { mutate: requestStakeDebitCard, isLoading: isRequestingCard } = useRequestStakeDebitCard();

  // Fetch user data for prefill
  const { data: dealsData } = useDeals();
  const isUmfUser = dealsData?.data?.data?.currentDeal?.is_umf_user === true;
  const { isFetching: isFetchingStakeDebit, data: stakeDebitData, refetch: refetchStakeDebit } = useStakeDebit({
    refetchInterval: (data) => {
      if (!data) return false;
      const svd = data?.data;
      if (isSVDEligible(svd) || isSVDDepositAccountCreated(svd)) return false;
      if (isPageVariant) {
        if (isSVDApplicationApproved(svd)) return 3 * 1000;
        if (isSVDApproved(svd)) return false;
        return 3 * 1000;
      }
      if (isSVDApproved(svd)) return false;
      return 3 * 1000;
    },
    refetchIntervalInBackground: true,
  });
  const { isLoading: isLoadingConnectedAccounts, data: connectedAccountsData } = useConnectedAccounts();
  const { data: ssoPrefillData, isLoading: isLoadingSsoPrefill, refetch: refetchSsoPrefill } = useSsoPrefill();

  const [isProcessingApplication, setIsProcessingApplication] = useState(false);
  const [isFinalizingCardRequest, setIsFinalizingCardRequest] = useState(false);

  const { openPlaid, isSavingAccount, plaidReady } = useConnectBankAccount({
    onSuccess: async () => {
      await queryClient.refetchQueries(APIQueryKeys.connectedAccounts);
      const followUp = plaidFollowUpRef.current;
      plaidFollowUpRef.current = null;
      followUp?.();
    },
  });

  const userData = useMemo(() => {
    const sso = ssoPrefillData || {};
    return {
      firstName: sso.firstname || "",
      lastName: sso.lastname || "",
      middleName: sso.middlename || "",
      email: sso.email || "",
      phoneNumber: sso["mobile number"] || "",
      address1: isUmfUser ? "" : sso.mailingstreet || "",
      address2: isUmfUser ? "" : sso.apt_number || "",
      city: isUmfUser ? "" : sso.mailingcity || "",
      state: isUmfUser ? "" : sso.mailingstate || "",
      zipCode: isUmfUser ? "" : sso.mailingpostalcode || "",
      birthdate: sso.dob ? appendISOTime(sso.dob) : "",
      ssn: sso["ssn number"] || "",
      // useSsoPrefill turns the stored name ("United States") into a code ("US"); this form works with the name.
      citizenship: citizenshipCodeOrLabelToLabel(sso.citizenship) || "United States",
      employerName: sso.employer_name || "",
      occupation: sso.occupation || "",
      industry: sso.industry || "",
      expectedMonthlyDeposit: sso.expected_monthly_deposit ? parseFloat(sso.expected_monthly_deposit) : '',
      expectedMonthlyWithdrawal: sso.expected_monthly_withdrawal ? parseFloat(sso.expected_monthly_withdrawal) : '',
    };
  }, [ssoPrefillData, isUmfUser]);

  const [step, setStep] = useState(0);
  const [stepInitialized, setStepInitialized] = useState(false);
  const [animClass, setAnimClass] = useState("ob-card-in");
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [pendingPlaidOpen, setPendingPlaidOpen] = useState(false);
  const [isVerifyNavigating, setIsVerifyNavigating] = useState(false);
  // The webhook-backed is_virtual_card_pin_set lags the widget, so this is what
  // moves the slot on once the user sets a PIN in place.
  const [pinJustSet, setPinJustSet] = useState(false);
  const [showAddressConfirmation, setShowAddressConfirmation] = useState(false);
  const [addressPrefillBaseline, setAddressPrefillBaseline] = useState(null);
  const bodyRef = useRef(null);
  const plaidFollowUpRef = useRef(null);
  const verifyStepRefetchStepRef = useRef(null);
  const battlecardVerifyRefetchRef = useRef(null);
  const verifyLoadingActiveRef = useRef(false);

  const runVerifyButtonLoading = useCallback(async (task) => {
    if (verifyLoadingActiveRef.current) return;
    verifyLoadingActiveRef.current = true;
    setIsVerifyNavigating(true);
    const start = Date.now();
    try {
      await task();
    } finally {
      const remaining = VERIFY_BUTTON_LOAD_MIN_MS - (Date.now() - start);
      if (remaining > 0) {
        await new Promise((resolve) => setTimeout(resolve, remaining));
      }
      verifyLoadingActiveRef.current = false;
      setIsVerifyNavigating(false);
    }
  }, []);

  useEffect(() => {
    if (!isFetchingStakeDebit && stakeDebitData) {
      if (isPageVariant && isSVDVerificationRejected(stakeDebitData?.data)) {
        redirectVerificationRejected();
        return;
      }
      if (isPageVariant && isSVDDepositAccountCreated(stakeDebitData?.data)) {
        redirectDepositAccountCreated();
        return;
      }
      if (isSVDEligible(stakeDebitData?.data)) {
        const progressState = dealsData?.data?.data?.currentDeal?.stake_checking_progress_state ?? 0;
        setStep(progressState > 4 ? progressState + 1 : progressState);
      } else if (
        isPageVariant
          ? (isSVDApproved(stakeDebitData?.data) || !hasSVDPhysicalCard(stakeDebitData?.data))
          : !hasSVDPhysicalCard(stakeDebitData?.data)
      ) {
        setStep(8);
      }
      if (!stepInitialized) setStepInitialized(true);
    }
  }, [dealsData, stakeDebitData, isFetchingStakeDebit, stepInitialized, isPageVariant, redirectVerificationRejected, redirectDepositAccountCreated]);

  // Refetch SSO prefill data when step changes
  useEffect(() => {
    refetchSsoPrefill()
  }, [step, refetchSsoPrefill]);

  useEffect(() => {
    if (step === 3 && ssoPrefillData) {
      setAddressPrefillBaseline((prev) => prev || ssoPrefillData);
    }
    if (step !== 3) {
      setAddressPrefillBaseline(null);
    }
  }, [step, ssoPrefillData]);

  useEffect(() => {
    if (step !== 3) {
      setShowAddressConfirmation(false);
    }
  }, [step]);

  const goNext = useCallback(() => {
    setAnimClass("ob-card-out");
    setTimeout(() => {
      setStep((s) => s + 1);
      setAnimClass("ob-card-in");
    }, 180);
  }, []);

  const balance = stakeDebitData?.data?.data?.available ?? 0;
  const monthlyCashback = dealsData?.data?.data?.currentDeal?.stake_pay?.stake_checking?.cashback_amount ?? 1000;
  const annualCashback = Math.round((monthlyCashback / 100) * 12);
  const isSyncteraUser = stakeDebitData?.data?.data?.is_synctera_user ?? false;
  const isStatusApproved = isSVDApproved(stakeDebitData?.data);
  const isStatusEligible = isSVDEligible(stakeDebitData?.data);
  const isBankConnected = (connectedAccountsData?.data?.data?.accounts?.length ?? 0) > 0;
  const virtualCardId = stakeDebitData?.data?.data?.virtual_card_id;
  const hasVirtualCard = hasSVDVirtualCard(stakeDebitData);
  const hasPhysicalCard = hasSVDPhysicalCard(stakeDebitData?.data);
  const isVirtualCardPinSet = stakeDebitData?.data?.data?.is_virtual_card_pin_set ?? false;
  const umfAddressPrefillBaseline = addressPrefillBaseline || ssoPrefillData;

  // Detects the virtual card newly landing — whether from the user's own request or the
  // webhook auto-issuing it in the background — by comparing against whether a card was
  // present on the previous render, kept in a ref. Fires the one-time interim PIN gate
  // only for that transition, not on every future visit to an already-carded wizard.
  const hadVirtualCardRef = useRef(hasVirtualCard);
  const [justGotVirtualCard, setJustGotVirtualCard] = useState(false);
  useEffect(() => {
    const hadCard = hadVirtualCardRef.current;
    hadVirtualCardRef.current = hasVirtualCard;
    if (!hadCard && hasVirtualCard) {
      setJustGotVirtualCard(true);
    }
  }, [hasVirtualCard]);

  const { data: activitiesData } = useActivities();
  const hasPendingTransaction = activitiesData?.data?.data?.hasPendingTransaction ?? false;

  // Reopens a fresh wizard after the interim PIN gate's inline widget succeeds — same
  // pattern as addCashAction — which naturally re-resolves past the now-dismissed gate
  // to whatever comes next.
  const onPinSetSuccess = useCallback(() => {
    setPinJustSet(true);
    setJustGotVirtualCard(false);
    setModalStack([]);
    renderStakeCheckingOnboardingStep(modalContext);
  }, [modalContext, setModalStack]);

  const steps = useMemo(() => buildSteps(goNext, setTermsAgreed, userData, {
    balance,
    annualCashback,
    onContactSupport: showIntercom,
    isSyncteraUser,
    isStatusApproved,
    isStatusEligible,
    isBankConnected,
    stakeDebitData,
    isBattlecardFlow: isPageVariant,
    includeCongrats: true,
    isUmfUser,
    hasVirtualCard,
    hasPhysicalCard,
    isVirtualCardPinSet,
    pinJustSet,
    hasPendingTransaction,
    justGotVirtualCard,
    virtualCardId,
    setPinStyles,
    onPinSetSuccess,
  }), [goNext, setTermsAgreed, userData, balance, annualCashback, isSyncteraUser, isStatusApproved, isStatusEligible, isBankConnected, stakeDebitData, isUmfUser, hasVirtualCard, hasPhysicalCard, isVirtualCardPinSet, pinJustSet, hasPendingTransaction, justGotVirtualCard, isPageVariant, showIntercom, virtualCardId, setPinStyles, onPinSetSuccess]);
  const total = useMemo(() => steps.length, [steps]);
  const verifyReviewStepIndex = useMemo(
    () => steps.findIndex((s) => s.reviewPending && s.reviewAction === "verify"),
    [steps],
  );
  const isBattlecardVerifyPending =
    isPageVariant &&
    isBattlecardRoute(pathname) &&
    stakeDebitData?.data &&
    (isSVDVerificationPendingStatus(stakeDebitData?.data) ||
      isSVDPendingVerification(stakeDebitData?.data));
  const displayStep =
    isBattlecardVerifyPending && !stepInitialized && verifyReviewStepIndex >= 0
      ? verifyReviewStepIndex
      : step;
  const current = useMemo(() => steps[displayStep], [steps, displayStep]);
  const isVerifyReviewStep =
    current?.reviewPending && current?.reviewAction === "verify";

  const congratsStepIndex = useMemo(() => steps.findIndex((s) => s.congrats), [steps]);

  useEffect(() => {
    if (!isBattlecardVerifyPending || stepInitialized || verifyReviewStepIndex < 0) return;
    setStep(verifyReviewStepIndex);
    setStepInitialized(true);
  }, [isBattlecardVerifyPending, stepInitialized, verifyReviewStepIndex]);

  useEffect(() => {
    if (isPageVariant && isBattlecardRoute(pathname)) {
      verifyStepRefetchStepRef.current = null;
      battlecardVerifyRefetchRef.current = null;
    }
  }, [isPageVariant, pathname]);

  useEffect(() => {
    if (!isPageVariant || !isBattlecardRoute(pathname) || !stakeDebitData?.data) return;
    const svd = stakeDebitData.data;
    if (!isSVDVerificationPendingStatus(svd) && !isSVDPendingVerification(svd)) return;

    const refKey = `battlecard-pending:${pathname}`;
    if (battlecardVerifyRefetchRef.current === refKey) return;

    battlecardVerifyRefetchRef.current = refKey;
    runVerifyButtonLoading(() => refetchStakeDebit());
  }, [isPageVariant, pathname, stakeDebitData, refetchStakeDebit, runVerifyButtonLoading]);

  useEffect(() => {
    if (!isVerifyReviewStep) {
      verifyStepRefetchStepRef.current = null;
      verifyLoadingActiveRef.current = false;
      return;
    }
    if (!stepInitialized || verifyStepRefetchStepRef.current === step) return;

    verifyStepRefetchStepRef.current = step;
    runVerifyButtonLoading(() => refetchStakeDebit());
  }, [isVerifyReviewStep, step, stepInitialized, refetchStakeDebit, runVerifyButtonLoading]);

  const goToStepIndex = useCallback((targetIndex) => {
    if (targetIndex < 0) return;
    setAnimClass("ob-card-out");
    setTimeout(() => {
      setStep(targetIndex);
      setAnimClass("ob-card-in");
    }, 180);
  }, []);

  const refetchAfterFunding = useCallback(() => {
    queryClient.refetchQueries(APIQueryKeys.stakeDebit);
    queryClient.refetchQueries(APIQueryKeys.connectedAccounts);
    queryClient.refetchQueries(APIQueryKeys.activities);
  }, [queryClient]);

  useEffect(() => {
    if (pendingPlaidOpen && plaidReady) {
      setPendingPlaidOpen(false);
      openPlaid();
    }
  }, [pendingPlaidOpen, plaidReady, openPlaid]);

  const goToCongratsOrFinish = useCallback(() => {
    if (congratsStepIndex >= 0) {
      goToStepIndex(congratsStepIndex);
    } else {
      finishWizard();
    }
  }, [congratsStepIndex, goToStepIndex, finishWizard]);

  // Requesting a card collapses the post-apply slot, which shifts congrats down an index.
  // Stepping forward by one would overshoot the end of the array and close the wizard, so
  // record the intent and let this effect resolve it against the rebuilt steps.
  const [pendingCongrats, setPendingCongrats] = useState(false);
  useEffect(() => {
    if (!pendingCongrats) return;
    setPendingCongrats(false);
    goToCongratsOrFinish();
  }, [pendingCongrats, goToCongratsOrFinish]);

  // Lock interstitial auto-advance
  useEffect(() => {
    if (current?.interstitial) {
      const timer = setTimeout(goNext, 3500);
      return () => clearTimeout(timer);
    }
  }, [step, current?.interstitial, goNext]);

  // Fire confetti on congrats step
  const confettiCanvasRef = useRef(null);
  const confettiInstanceRef = useRef(null);
  useEffect(() => {
    if (current?.congrats && confettiCanvasRef.current) {
      if (!confettiInstanceRef.current) {
        confettiInstanceRef.current = confetti.create(confettiCanvasRef.current, { resize: true, useWorker: true });
      }
      const fire = confettiInstanceRef.current;
      const palette = ["#ffffff", "#f0f0f0", "#54FF99", "#ffffff", "#e0e0e0", "#6EFFAA"];
      const whites = ["#ffffff", "#f0f0f0", "#e0e0e0", "#d4d4d4"];
      const greens = ["#54FF99", "#6EFFAA", "#3BD87A"];
      fire({ particleCount: 30, spread: 50, origin: { x: 0.5, y: 0.55 }, colors: palette, scalar: 0.7, gravity: 0.35, ticks: 220, startVelocity: 22, shapes: ["circle"] });
      setTimeout(() => fire({ particleCount: 18, spread: 70, origin: { x: 0.32, y: 0.5 }, colors: whites, scalar: 0.55, gravity: 0.3, ticks: 200, startVelocity: 18, shapes: ["circle"] }), 200);
      setTimeout(() => fire({ particleCount: 18, spread: 70, origin: { x: 0.68, y: 0.5 }, colors: whites, scalar: 0.55, gravity: 0.3, ticks: 200, startVelocity: 18, shapes: ["circle"] }), 320);
      setTimeout(() => fire({ particleCount: 10, spread: 90, origin: { x: 0.5, y: 0.48 }, colors: greens, scalar: 0.9, gravity: 0.2, ticks: 280, startVelocity: 14, shapes: ["star"], flat: true }), 480);
      setTimeout(() => fire({ particleCount: 8, spread: 120, origin: { x: 0.5, y: 0.42 }, colors: ["#ffffff", "#54FF99"], scalar: 0.4, gravity: 0.15, ticks: 320, startVelocity: 8, shapes: ["circle"] }), 750);
    } else if (!current?.congrats && confettiInstanceRef.current) {
      confettiInstanceRef.current.reset();
    }
  }, [step, current?.congrats]);

  const handleClose = () => {
    exitWizard();
  };

  const handleSecondary = () => {
    // Dismiss the interim PIN gate in place rather than exiting — the slot rebuilds into
    // whatever comes next (physical card offer, etc.) without moving the step index.
    if (current?.dismissPinGate) {
      setJustGotVirtualCard(false);
      return;
    }
    if (current?.congrats) {
      finishWizard();
      return;
    }
    if (current?.skipToHome) {
      finishWizard();
      return;
    }
    // Skipping a card request is a plain dismissal — there is nothing to celebrate yet.
    if (current?.skipToExit) {
      handleClose();
      return;
    }
    if (current?.skipToFinish) {
      if (isPageVariant) {
        goToCongratsOrFinish();
      } else {
        handleClose();
      }
      return;
    }
    handleClose();
  };

  if (
    (!stepInitialized && !isBattlecardVerifyPending) ||
    (isLoadingConnectedAccounts && !isBattlecardVerifyPending) ||
    (isLoadingSsoPrefill && !isBattlecardVerifyPending)
  ) {
    return <WizardSkeleton onClose={handleClose} variant={variant} hideMilestone={hideMilestone} />;
  }

  if (!current) {
    exitWizard();
    return null;
  }

  const saveStepAndAdvance = (payload) => {
    saveProgressState(payload, {
      onSuccess: () => {
        setShowAddressConfirmation(false);
        goNext();
        queryClient.refetchQueries(APIQueryKeys.deals);
      },
      onError: (e) => dispatchAPIErrorToast({ message: e?.response?.data?.error }),
    });
  };

  const getStepPayload = (stepIndex, card) => {
    switch (stepIndex) {
      case 0: return {
        step: 1,
        first_name: getInputByLabel(card, "First Name")?.value || "",
        middle_name: getInputByLabel(card, "Middle Name (optional)")?.value || "",
        last_name: getInputByLabel(card, "Last Name")?.value || "",
        email: getInputByLabel(card, "Email")?.value || "",
      };
      case 1: {
        const raw = card.querySelector(".dob-hidden-value")?.value || "";
        const [mm, dd, yyyy] = raw.split("/");
        return { step: 2, dob: raw ? `${yyyy}-${mm}-${dd}` : "" };
      }
      case 2: return {
        step: 3,
        mobile_phone_number: (getInputByLabel(card, "Phone Number")?.value || "").replace(/\D/g, ""),
      };
      case 3: return {
        step: 4,
        street_address: card.querySelector(".ob-street-address-value")?.value || "",
        apt_number: getInputByLabel(card, "Apt / Suite (optional)")?.value || "",
        zip_code: getInputByLabel(card, "ZIP Code")?.value || "",
        city: getInputByLabel(card, "City")?.value || "",
        mailing_state: getInputByLabel(card, "State")?.value || "",
      };
      case 5: return {
        step: 5,
        ssn: (getInputByLabel(card, "Social Security Number")?.value || "").replace(/\D/g, ""),
      };
      case 6: return {
        step: 6,
        citizenship: getInputByLabel(card, "Citizenship")?.value || "",
        employer_name: getInputByLabel(card, "Employer Name")?.value || "",
        occupation: getInputByLabel(card, "Occupation")?.value || "",
        industry: getInputByLabel(card, "Industry")?.value || "",
        expected_monthly_deposit: parseFloat(getInputByLabel(card, "Monthly Deposits*")?.value) || 0,
        expected_monthly_withdrawal: parseFloat(getInputByLabel(card, "Monthly Withdrawals*")?.value) || 0,
      };
      default: return null;
    }
  };

  const handleNext = async () => {
    if (current.setPinNowAction) {
      // embedded: true so SetPinModal doesn't pop the modal stack itself — we drive what
      // happens next, since this is opened from deep inside an already-open wizard modal.
      renderSetPin(modalContext, {
        cardId: virtualCardId,
        styles: setPinStyles,
        onSuccess: () => {
          setPinJustSet(true);
          onModalClose();
        },
      });
      return;
    }
    if (current.congrats) {
      finishWizard();
      return;
    }
    if (current.reviewPending) {
      if (current.reviewAction === "verify") {
        await runVerifyButtonLoading(() => refetchStakeDebit());
        navigate("/verify-stake-checking", { state: { previousPathname: pathname } });
        if (!isPageVariant) exitWizard();
        return;
      }
      if (current.reviewAction === "support") {
        showIntercom();
      }
      exitWizard();
      return;
    }
    if (current.addCashAction) {
      renderGetBankAmountDetails(modalContext, {
        onSuccess: () => {
          refetchAfterFunding();
          setModalStack([]);
          renderStakeCheckingOnboardingStep(modalContext);
        },
      });
      return;
    }
    if (current.connectBankAction) {
      if (!isPageVariant) {
        openPlaid();
        return;
      }
      if (isBankConnected) {
        finishWizard();
        return;
      }
      plaidFollowUpRef.current = () => {
        refetchAfterFunding();
        finishWizard();
      };
      if (plaidReady) {
        openPlaid();
      } else {
        setPendingPlaidOpen(true);
      }
      return;
    }
    if (current.requestCardAction) {
      const cardType = current.requestCardAction;
      setIsFinalizingCardRequest(true);
      requestStakeDebitCard(
        cardType,
        {
          onSuccess: async () => {
            await queryClient.refetchQueries(APIQueryKeys.stakeDebit);
            setIsFinalizingCardRequest(false);
            // A new virtual card rebuilds this same slot into the Set PIN step; only the
            // physical request ends the sequence and moves on to congrats.
            if (cardType !== "VIRTUAL") setPendingCongrats(true);
          },
          onError: async (e) => {
            // The webhook can issue the card between our last poll and this click, which
            // comes back as a 409. That is the outcome the user wanted, so refetch and
            // only surface the error if the card really is missing.
            await queryClient.refetchQueries(APIQueryKeys.stakeDebit);
            setIsFinalizingCardRequest(false);
            const refreshed = queryClient.getQueryData(APIQueryKeys.stakeDebit);
            const landed =
              cardType === "VIRTUAL"
                ? hasSVDVirtualCard(refreshed)
                : hasSVDPhysicalCard(refreshed?.data);
            if (landed) {
              if (cardType !== "VIRTUAL") setPendingCongrats(true);
              return;
            }
            dispatchAPIErrorToast({ message: e?.response?.data?.error });
          },
        },
      );
      return;
    }
    if (current.validate && bodyRef.current) {
      if (!current.validate(bodyRef.current)) return;
    }
    if (current.requiresTerms && !termsAgreed) return;
    if (current.requiresTerms) {
      setIsProcessingApplication(true);
      onApplyingChange?.(true);
      applyStakeChecking(
        {},
        {
          onSuccess: async () => {
            await queryClient.refetchQueries(APIQueryKeys.stakeDebit);
            await queryClient.refetchQueries(APIQueryKeys.deals);
            setIsProcessingApplication(false);
            if (isPageVariant) {
              const refreshedStakeDebit = queryClient.getQueryData(APIQueryKeys.stakeDebit);
              if (onApplied) {
                onApplied(refreshedStakeDebit?.data);
              } else if (isSVDVerificationRejected(refreshedStakeDebit?.data)) {
                redirectVerificationRejected();
              } else if (isSVDVerificationPendingStatus(refreshedStakeDebit?.data)) {
                setStep(8);
              } else if (isSVDDepositAccountCreated(refreshedStakeDebit?.data)) {
                redirectDepositAccountCreated();
              }
            }
          },
          onError: (e) => {
            setIsProcessingApplication(false);
            onApplyingChange?.(false);
            dispatchAPIErrorToast({ message: e?.response?.data?.error });
          },
        },
      );
      return;
    }
    const payload = bodyRef.current ? getStepPayload(step, bodyRef.current) : null;
    if (payload) {
      const shouldConfirmUmfAddress =
        step === 3 &&
        shouldShowUmfAddressConfirmModal(isUmfUser, umfAddressPrefillBaseline, payload);

      if (shouldConfirmUmfAddress) {
        setShowAddressConfirmation(true);
        return;
      }
      saveStepAndAdvance(payload);
    }
  };

  const handleKeepAddress = () => {
    const payload = bodyRef.current ? getStepPayload(step, bodyRef.current) : null;
    if (payload) {
      saveStepAndAdvance(payload);
    }
  };

  const handleBack = () => {
    setShowAddressConfirmation(false);
    setAnimClass("ob-card-out");
    setTimeout(() => {
      setStep((s) => {
        let prev = s - 1;
        while (prev > 0 && steps[prev]?.interstitial) prev--;
        return Math.max(0, prev);
      });
      setAnimClass("ob-card-in");
    }, 180);
  };

  const pct = Math.round(((displayStep + 1) / total) * 100);
  const showBack = displayStep > 0 && !current.noBack && !current.interstitial;
  const isVerifyButtonLoading = isVerifyReviewStep && isVerifyNavigating;
  const isPrimaryLoading =
    isSavingProgress ||
    isApplying ||
    isRequestingCard ||
    isFinalizingCardRequest ||
    isProcessingApplication ||
    isSavingAccount ||
    isVerifyButtonLoading ||
    (current.connectBankAction && pendingPlaidOpen && !plaidReady);

  const hideModalChrome = current.interstitial || (current.congrats && !current.pinPromptVariant);
  const milestoneState = getMemberSetupMilestoneState(displayStep, current);
  const pageShowBack = !current.interstitial && !current.noBack && (showBack || isPageVariant);

  const addressConfirmationOverlay = showAddressConfirmation ? (
    <AddressConfirmationOverlay
      onEditAddress={() => setShowAddressConfirmation(false)}
      onKeepAddress={handleKeepAddress}
      isSaving={isSavingProgress}
    />
  ) : null;

  const wizardContent = (
    <div className={`sc-onboarding-step${isPageVariant ? " sc-onboarding-step--page" : ""}`}>
      <canvas ref={confettiCanvasRef} className="ob-confetti-canvas" />
      {/* Header — modal only */}
      {!hideModalChrome && !isPageVariant && (
        <div className="sc-ob-header">
          <button
            className={`sc-ob-back-btn${showBack ? "" : " hidden"}`}
            onClick={handleBack}
            disabled={!showBack}
            aria-label="Go back"
          >
            <svg viewBox="0 0 10 10" fill="none">
              <path
                d="M7 1L3 5L7 9"
                stroke="#8B8FA3"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <span className="sc-ob-header-title">Set up Stake Debit</span>
          <button className="sc-ob-close-btn" onClick={handleClose} aria-label="Close">
            <svg viewBox="0 0 12 12" fill="none">
              <path
                d="M1 1L11 11M11 1L1 11"
                stroke="#8B8FA3"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
      )}

      {/* Linear progress — modal only */}
      {!hideModalChrome && !isPageVariant && (
        <div className="sc-ob-progress">
          <div
            className="sc-ob-progress-track"
            role="progressbar"
            aria-label={`Stake Checking onboarding, step ${step + 1} of ${total}`}
            aria-valuemin={1}
            aria-valuemax={total}
            aria-valuenow={step + 1}
            aria-valuetext={`Step ${step + 1} of ${total}`}
          >
            <div
              className="sc-ob-progress-fill"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      )}

      {!hideModalChrome && !isPageVariant && <div className="sc-ob-divider" />}

      {/* Step body */}
      <div className={`sc-ob-step-body ${animClass}`} ref={bodyRef}>
        {current.custom ? (
          current.custom
        ) : (
          <>
            <div className="sc-ob-title ob-stagger">{current.title}</div>
            <div className="sc-ob-subtitle ob-stagger">
              {current.subtitle}
            </div>
            <div className="ob-stagger">{current.body}</div>
          </>
        )}
      </div>

      {/* Action bar */}
      {!current.interstitial && (current.btn || current.btn2) && (
        <div className="sc-ob-actions">
          {current.btn && (
            <button
              className={`ob-btn-primary${current.requiresTerms && !termsAgreed ? " ob-btn-disabled" : ""}${isPrimaryLoading ? " ob-btn-loading" : ""}`}
              onClick={handleNext}
              disabled={
                (current.requiresTerms && !termsAgreed) ||
                isPrimaryLoading ||
                (current.connectBankAction && !plaidReady && !isBankConnected)
              }
            >
              {current.btn}
              {isVerifyReviewStep
                ? isVerifyButtonLoading && (
                    <CircularProgress size={18} thickness={5} className="ob-btn-spinner" />
                  )
                : isPrimaryLoading && (
                    <CircularProgress size={18} thickness={5} className="ob-btn-spinner" />
                  )}
            </button>
          )}
          {current.btn2 && (
            <button className="ob-btn-secondary" onClick={handleSecondary} disabled={isPrimaryLoading}>
              {current.btn2}
            </button>
          )}
        </div>
      )}

      {!isPageVariant && addressConfirmationOverlay}
    </div>
  );

  if (isPageVariant) {
    if (current.congrats && !current.pinPromptVariant) {
      return (
        <div className="bc-member-setup-page-chromeless">
          {wizardContent}
        </div>
      );
    }

    return (
      <div
        className={`bc-member-setup-inner${current.interstitial ? " bc-member-setup-inner--interstitial" : ""}${showAddressConfirmation ? " bc-member-setup-inner--address-confirm" : ""}`}
      >
        <div className="bc-member-setup-header">
          <button
            type="button"
            className={`bc-member-setup-back${pageShowBack ? "" : " hidden"}`}
            onClick={showBack ? handleBack : handleClose}
            disabled={!pageShowBack}
            aria-label="Go back"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
              <path d="M7.5 2.5L4 6L7.5 9.5" stroke="#8B8FA3" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <span className="bc-member-setup-title">Become a Member</span>
        </div>
        {hideMilestone ? null : <MemberSetupMilestoneProgress milestone={milestoneState} />}
        <div className="bc-member-setup-divider" />
        {wizardContent}
        {addressConfirmationOverlay}
      </div>
    );
  }

  return wizardContent;
}

/* ═══════════════════════════════════════════
   MODAL RENDER FUNCTION
   ═══════════════════════════════════════════ */
export default function renderStakeCheckingOnboardingStep(
  modalContext,
) {
  const {
    updateModalComponent,
    pushModalStack,
    setModalVariant,
    setModalPromoComponent,
    setModalTitle,
    setModalAriaLabel,
    setModalClassName,
  } = modalContext;

  updateModalComponent(() => (
    <StakeCheckingWizard />
  ));
  pushModalStack(() =>
    renderStakeCheckingOnboardingStep(modalContext),
  );
  setModalPromoComponent(null);
  setModalTitle("");
  setModalAriaLabel?.("Set up Stake debit");
  setModalClassName("hide-header no-full-screen");
  setModalVariant(modalVariants.stepByStepDialog);
}

export {
  INDEX_TO_STEP_KEY as StakeCheckingProgressIndexToStepKey,
  getSecondaryAgreementLabels,
  getSecondaryAgreementLabels as getAgreementLabels,
  getMemberSetupMilestonePhase,
  getMemberSetupMilestoneState,
};